import { a11yProps } from "@/components/Tabs/A11yProps.tsx";
import { useCaseTask } from "@/contexts/CaseTasksProvider.tsx";
import { useFetchNotificationsForUser } from "@/queries/useFetchNotificationsForUser.ts";
import { Badge, Box } from "@mui/material";
import Tab from "@mui/material/Tab";
import { BellRing, CheckSquare } from "lucide-react";

interface Props {
    caseUUID: string;
    label: string;
    index: number;
    limitToCategory?: string;
}

const TabWithBadge = ({ caseUUID, label, index, limitToCategory, ...props }: Props) => {
    const { caseTasksPending } = useCaseTask();
    const tasksForTab = caseTasksPending.filter((task) => task.caseStatuses.includes(label));
    const badgeCount = tasksForTab.length;
    const badgeCountOverdue = tasksForTab.filter((task) => task.overdue).length ?? 0;
    const { data: notificationData = [] } = useFetchNotificationsForUser();

    const filteredNotifications = limitToCategory
        ? notificationData.filter(
              (notification) =>
                  notification.note.tag === limitToCategory && notification.hasUnreadNotification,
          )
        : notificationData.filter((notification) => notification.hasUnreadNotification);

    return (
        <Tab
            {...props}
            {...a11yProps(index)}
            label={
                <Box display="flex" alignItems="center">
                    {label}
                    {filteredNotifications.length > 0 && (
                        <Badge
                            badgeContent={filteredNotifications.length}
                            color="error"
                            sx={{
                                pl: 1,
                                "& .MuiBadge-badge": {
                                    backgroundColor: "red",
                                    color: "white",
                                },
                            }}
                        >
                            <BellRing size={20} color="red" />
                        </Badge>
                    )}
                    {badgeCount > 0 && (
                        <Badge
                            badgeContent={badgeCount}
                            color={badgeCountOverdue > 0 ? "error" : "primary"}
                            sx={{
                                pl: 1,
                                "& .MuiBadge-badge": {
                                    backgroundColor: "green",
                                    color: "white",
                                },
                            }}
                        >
                            <CheckSquare size={20} color="green" />
                        </Badge>
                    )}
                </Box>
            }
            wrapped={label.split(" ").length > 0}
        />
    );
};

export default TabWithBadge;
